import React, { useState, useMemo, useEffect } from "react";
import { Modal } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { RootStatePlatform, platformActions } from "../../store/platform-slice";
import {
  getCategoryColor,
  getBiomarkerCategoryColor,
  MetricType,
  enPrecisionTestKind,
} from "../../store/helper";
import { systemGroupType } from "../../../../providers/dataPlatformReportTypes";
import {
  getPlatformData,
  toggleSystemApprovals,
} from "../../../../common/network/Api";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import ArrowDown from "../../svg/ArrowDown";
import EyeSlashIcon from "../../svg/EyeSlashIcon";
import CheckSingleIcon from "../../svg/CheckSingleIcon";
import ClockIcon from "../../svg/ClockIcon";
import ScrollableWrapper from "../common/ScrollableWrapper";
import {
  isOppositeGenderSystem,
  resolveClientGender,
} from "../../hooks/useClientGender";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import {
  BloodBiomarkersList,
  BreathBiomarkersList,
} from "../score/SidePanelShared";
import { BiomarkerItem, BreathMetricGroup } from "../score/usePanelData";

const selectSystems = createSelector(
  (state: RootStatePlatform) =>
    state.platform.dataPlatformServer.score?.systems,
  (systems) => systems || []
);

const selectDashboardData = (state: RootStatePlatform) =>
  state.platform.dataPlatformServer.dashboard;

type Props = {
  open: boolean;
  onClose: () => void;
  clientId: string;
  userRole: number;
  clientName: string;
  clientApproval?: number;
  clientGender?: number;
  onSaveSuccess: () => void;
};

const ReviewHealthScoresModal = ({
  open,
  onClose,
  clientId,
  userRole,
  clientName,
  clientApproval,
  clientGender,
  onSaveSuccess,
}: Props) => {
  const isReview = clientApproval === 1;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  const systems = useSelector(selectSystems);
  const dashboardData = useSelector(selectDashboardData);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [approvals, setApprovals] = useState<Record<number, number>>({});
  const [expandedSystems, setExpandedSystems] = useState<Set<number>>(
    new Set()
  );
  const [showOthers, setShowOthers] = useState(false);

  // Collapse the already-reviewed section every time the modal opens.
  useEffect(() => {
    if (open) setShowOthers(false);
  }, [open]);

  // Fetch platform data when modal opens (skip if data already exists in Redux)
  useEffect(() => {
    if (open && clientId) {
      if (systems.length > 0) {
        return;
      }
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const data = await getPlatformData(clientId, userRole);
          dispatch(platformActions.updateDataPlatformServer(data));
        } catch (error) {
          console.error("Error fetching platform data:", error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, userRole, dispatch]);

  // Filter systems by gender (exclude opposite sexual wellness system).
  // Resolved from the report that carries these very systems, with the
  // caller's numeric gender only as a fallback: callers disagreed about where
  // gender comes from (client list row vs questionnaire answers), and a screen
  // that hadn't loaded the questionnaire used to pass an empty value here —
  // which silently hid the male system, and with it any approval pending on it.
  const gender = useMemo(
    () => resolveClientGender(dashboardData?.gender, clientGender),
    [dashboardData, clientGender]
  );

  const filteredSystems = useMemo(() => {
    return systems.filter(
      (sys: systemGroupType) => !isOppositeGenderSystem(sys.system, gender)
    );
  }, [systems, gender]);

  // Sync approvals when systems data arrives
  useEffect(() => {
    if (filteredSystems.length > 0) {
      const initial: Record<number, number> = {};
      filteredSystems.forEach((sys: systemGroupType) => {
        initial[sys.system] = sys.approval ?? 1;
      });
      setApprovals(initial);
    }
  }, [filteredSystems]);

  const pendingCount = useMemo(
    () => Object.values(approvals).filter((v) => v !== 2 && v !== 4).length,
    [approvals]
  );

  const allReviewed = pendingCount === 0;

  // Only the scores awaiting review are listed by default; the rest sit
  // behind the "Show other scores" divider (design 16330:67052). The split
  // uses the server-side approval, not the live local one, so a row doesn't
  // jump sections the moment it's approved or hidden.
  const pendingSystems = useMemo(
    () =>
      filteredSystems.filter(
        (sys: systemGroupType) =>
          (sys.approval ?? 1) !== 2 && (sys.approval ?? 1) !== 4
      ),
    [filteredSystems]
  );
  const otherSystems = useMemo(
    () =>
      filteredSystems.filter(
        (sys: systemGroupType) =>
          (sys.approval ?? 1) === 2 || (sys.approval ?? 1) === 4
      ),
    [filteredSystems]
  );

  // Nothing pending (the "Edit health scores" mode) — show everything.
  const othersVisible = showOthers || pendingSystems.length === 0;

  const handleSetApproval = (systemId: number, value: number) => {
    setApprovals((prev) => ({ ...prev, [systemId]: value }));
  };

  // Bulk actions act on the scores awaiting review; with nothing pending
  // (edit mode) they keep applying to every system.
  const bulkTargets =
    pendingSystems.length > 0 ? pendingSystems : filteredSystems;

  const handleRejectAll = () => {
    setApprovals((prev) => {
      const next = { ...prev };
      bulkTargets.forEach((sys: systemGroupType) => {
        next[sys.system] = 2;
      });
      return next;
    });
  };

  const handleApproveAll = () => {
    setApprovals((prev) => {
      const next = { ...prev };
      bulkTargets.forEach((sys: systemGroupType) => {
        next[sys.system] = 4;
      });
      return next;
    });
  };

  const handleToggleExpand = (systemId: number) => {
    setExpandedSystems((prev) => {
      const next = new Set(prev);
      if (next.has(systemId)) {
        next.delete(systemId);
      } else {
        next.add(systemId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const payload: Record<string, string | number> = { CustomerID: clientId };
    Object.entries(approvals).forEach(([systemId, status]) => {
      const name = MetricType[Number(systemId)];
      if (name) payload[name] = status;
    });

    setIsSaving(true);
    try {
      await toggleSystemApprovals(payload);
      dispatch(platformActions.clearDataPlatformServer());
      onSaveSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving system approvals:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div style={styles.modalContainer(theme)}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.title(theme)}>
              {isReview ? "Review health scores" : "Edit health scores"}
            </div>
            <div style={styles.subtitle(theme)}>{clientName}</div>
          </div>
          <div style={styles.closeButton} onClick={onClose}>
            <XIconSvg />
          </div>
        </div>

        {/* Summary Bar */}
        {!isLoading && (
          <div style={styles.summaryBar(pendingCount, theme)}>
            <div style={styles.summaryLeft}>
              <ClockIcon fill={theme.colors_ds.NEUTRAL_TEXT_DEFAULT} />
              <span style={styles.summaryPendingText(theme)}>
                {pendingCount} pending
              </span>
            </div>

            <div style={styles.summaryRight}>
              <span style={styles.hideAllLink(theme)} onClick={handleRejectAll}>
                Hide all
              </span>
              <span
                style={styles.approveAllLink(theme)}
                onClick={handleApproveAll}
              >
                Approve all
              </span>
            </div>
          </div>
        )}

        {/* System List */}
        <ScrollableWrapper customPadding={"0"}>
          <div style={styles.systemList}>
            {isLoading ? (
              <Skeleton
                count={8}
                height={44}
                borderRadius={8}
                style={{ marginBottom: 8 }}
              />
            ) : (
              <>
                {pendingSystems.map((system: systemGroupType) => (
                  <SystemRow
                    key={system.system}
                    system={system}
                    approvalValue={approvals[system.system] ?? 1}
                    isExpanded={expandedSystems.has(system.system)}
                    onToggleExpand={() => handleToggleExpand(system.system)}
                    onSetApproval={(value: number) =>
                      handleSetApproval(system.system, value)
                    }
                    dashboardData={dashboardData}
                    theme={theme}
                  />
                ))}
                {pendingSystems.length > 0 && otherSystems.length > 0 && (
                  <div style={styles.othersToggleRow}>
                    <div style={styles.othersToggleLine(theme)} />
                    <div
                      style={styles.othersToggle(theme)}
                      onClick={() => setShowOthers((prev) => !prev)}
                    >
                      <div
                        style={{
                          ...styles.arrowIcon,
                          transform: showOthers
                            ? "rotate(0deg)"
                            : "rotate(-90deg)",
                        }}
                      >
                        <ArrowDown width={14} height={14} />
                      </div>
                      {showOthers ? "Hide other scores" : "Show other scores"}
                    </div>
                    <div style={styles.othersToggleLine(theme)} />
                  </div>
                )}
                {othersVisible &&
                  otherSystems.map((system: systemGroupType) => (
                    <SystemRow
                      key={system.system}
                      system={system}
                      approvalValue={approvals[system.system] ?? 1}
                      isExpanded={expandedSystems.has(system.system)}
                      onToggleExpand={() => handleToggleExpand(system.system)}
                      onSetApproval={(value: number) =>
                        handleSetApproval(system.system, value)
                      }
                      dashboardData={dashboardData}
                      theme={theme}
                    />
                  ))}
              </>
            )}
          </div>
        </ScrollableWrapper>

        {/* Save Button */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <PrimaryButton
            label={"Save"}
            onClick={handleSave}
            isDisable={!allReviewed || isLoading || isSaving}
          />
        </div>
      </div>
    </Modal>
  );
};

// --- SystemRow ---

type SystemRowProps = {
  system: systemGroupType;
  approvalValue: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSetApproval: (value: number) => void;
  dashboardData: any;
  theme: Theme;
};

const SystemRow = ({
  system,
  approvalValue,
  isExpanded,
  onToggleExpand,
  onSetApproval,
  dashboardData,
  theme,
}: SystemRowProps) => {
  const scoreColor = getCategoryColor(
    "%",
    system.score,
    theme,
    system.scoreCategory
  );
  const isRejected = approvalValue === 2;
  const isApproved = approvalValue === 4;

  const breathSections = useMemo(() => {
    if (!isExpanded) return [];
    const weights: any[] = (system as any).weights || [];
    const kinds: { weight: any; name: string; kindNumber: number }[] = [];
    const k1 = weights.find((w: any) => w.kind === 1);
    const k2 = weights.find((w: any) => w.kind === 2);
    if (k1) kinds.push({ weight: k1, name: "Breath Resting", kindNumber: 1 });
    if (k2) kinds.push({ weight: k2, name: "Breath Active", kindNumber: 2 });

    return kinds.map(({ weight: kindWeight, name, kindNumber }) => {
      const groups: BreathMetricGroup[] = [];

      ((system as any).breathBiomarkers || []).forEach((metric: any) => {
        const dashMetric = dashboardData?.metrics?.find(
          (m: any) => m.id === metric.id
        );
        if (!dashMetric) return;

        const biomarkers = (metric.biomarkers || [])
          .map((nested: any) => {
            const dashBio = dashboardData?.breathbiomarkers?.find(
              (b: any) => b.id === nested.id
            );
            if (!dashBio || Number(dashBio.kind) !== kindNumber) return null;
            const bioCategoryColor = getBiomarkerCategoryColor(
              dashBio.scoreCategory,
              theme,
              "biomarkers"
            );
            return {
              label: dashBio.label,
              value: dashBio.value,
              unit: dashBio.unit,
              categoryColor: bioCategoryColor,
            };
          })
          .filter(Boolean) as BreathMetricGroup["biomarkers"];

        if (biomarkers.length > 0) {
          const metricCategoryColor = getCategoryColor(
            "%",
            dashMetric.score,
            theme,
            dashMetric.scoreCategory
          );

          groups.push({
            metricName: dashMetric.label,
            weight: metric.weight ?? null,
            weightMax: metric.weightMax ?? null,
            categoryColor: metricCategoryColor,
            biomarkers,
          });
        }
      });

      return {
        name,
        groups,
        total: kindWeight.weight || 0,
        weightMax: kindWeight.total || 0,
      };
    });
  }, [isExpanded, system, dashboardData, theme]);

  const bloodGroups = useMemo(() => {
    if (!isExpanded) return [];
    const weights: any[] = (system as any).weights || [];
    const groups: {
      name: string;
      total: number;
      weightMax: number;
      items: BiomarkerItem[];
    }[] = [];
    const byKind: Record<number, BiomarkerItem[]> = {};

    (system.bloodBiomarkers || []).forEach((bb: any) => {
      const dashBio = dashboardData?.bloodbiomarkers?.find(
        (d: any) => d.id === bb.id
      );
      if (!dashBio) return;
      const kind = Number(dashBio.kind);
      const categoryColor = getBiomarkerCategoryColor(
        dashBio.scoreCategory,
        theme,
        "biomarkers"
      );
      if (!byKind[kind]) byKind[kind] = [];
      byKind[kind].push({
        label: dashBio.shortname || dashBio.label,
        value: dashBio.value,
        unit: dashBio.unit,
        weight: bb.weight ?? null,
        weightMax: bb.weightMax ?? null,
        categoryColor,
      });
    });

    // Match weight entries (skip breath kinds 1,2)
    weights
      .filter((w: any) => w.kind !== 1 && w.kind !== 2 && w.kind !== 0)
      .forEach((w: any) => {
        const kind = Number(w.kind);
        if (byKind[kind]?.length) {
          groups.push({
            name: w.name,
            total: w.weight ?? 0,
            weightMax: w.total ?? 0,
            items: byKind[kind],
          });
          delete byKind[kind];
        }
      });

    // Orphaned biomarkers whose kind has no matching weight entry
    Object.entries(byKind).forEach(([kind, items]) => {
      if (items.length > 0) {
        groups.push({
          name: enPrecisionTestKind[Number(kind)] || "Other",
          total: 0,
          weightMax: 0,
          items,
        });
      }
    });

    return groups;
  }, [isExpanded, system, dashboardData, theme]);

  const hasExpandedContent =
    breathSections.some(
      (section) =>
        section.groups.length > 0 &&
        section.groups.some((g) => g.biomarkers.length > 0 && g.weight !== 0)
    ) ||
    bloodGroups.some((group) =>
      group.items.some((item) => item.weight !== 0)
    );

  return (
    <div style={styles.systemRowContainer(theme, !isRejected && !isApproved)}>
      {/* Collapsed Row */}
      <div style={styles.systemRowHeader} onClick={onToggleExpand}>
        <div style={styles.systemRowLeft}>
          <div
            style={{
              ...styles.arrowIcon,
              transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          >
            <ArrowDown width={14} height={14} />
          </div>
          <span style={styles.systemName(theme)}>{system.name}</span>
        </div>
        <div style={styles.systemRowRight} onClick={(e) => e.stopPropagation()}>
          <div style={styles.scoreBadge(scoreColor)}>
            {Math.round(system.score)}%
          </div>
          <button
            style={styles.iconButton(isRejected, theme)}
            onClick={() => onSetApproval(2)}
            title="Reject"
          >
            <EyeSlashIcon fill={isRejected ? "#FFFFFF" : "#6D7075"} />
          </button>
          <button
            style={styles.iconButton(isApproved, theme)}
            onClick={() => onSetApproval(4)}
            title="Approve"
          >
            <CheckSingleIcon fill={isApproved ? "#FFFFFF" : "#6D7075"} />
          </button>
        </div>
      </div>

      {/* Expanded Content - Empty state */}
      {isExpanded && !hasExpandedContent && (
        <div style={styles.expandedContent}>
          <div style={styles.divider(theme)} />
          <div style={styles.emptyState(theme)}>
            No biomarkers are connected to this system.
          </div>
        </div>
      )}

      {/* Expanded Content - Biomarker details */}
      {isExpanded && hasExpandedContent && (
        <div style={styles.expandedContent}>
          <div style={styles.divider(theme)} />

          {breathSections.map((section, sIdx) =>
            section.groups.length > 0 &&
            section.groups.some(
              (g) => g.biomarkers.length > 0 && g.weight !== 0
            ) ? (
              <div key={sIdx} style={styles.biomarkerSection}>
                <div style={styles.sectionLabelRow}>
                  <div style={styles.sectionLabel(theme)}>{section.name}</div>
                  {section.weightMax > 0 && (
                    <span style={styles.sectionWeight(theme)}>
                      {parseFloat(section.total.toFixed(2))}
                      <span style={styles.sectionWeightMax(theme)}>
                        /{parseFloat(section.weightMax.toFixed(2))}%
                      </span>
                    </span>
                  )}
                </div>
                <BreathBiomarkersList groups={section.groups} theme={theme} />
              </div>
            ) : null
          )}

          {bloodGroups
            .filter((group) => group.items.some((item) => item.weight !== 0))
            .map((group, gIdx) => (
            <div key={gIdx} style={styles.biomarkerSection}>
              <div style={styles.sectionLabelRow}>
                <div style={styles.sectionLabel(theme)}>{group.name}</div>
                {group.weightMax > 0 && (
                  <span style={styles.sectionWeight(theme)}>
                    {parseFloat(group.total.toFixed(2))}
                    <span style={styles.sectionWeightMax(theme)}>
                      /{parseFloat(group.weightMax.toFixed(2))}%
                    </span>
                  </span>
                )}
              </div>
              <BloodBiomarkersList items={group.items} theme={theme} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewHealthScoresModal;

// --- Styles ---

const styles = {
  modalContainer: (theme: Theme): React.CSSProperties => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    height: "80vh",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: 8,
    padding: 24,
    outline: "none",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  }),
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 8,
  } as React.CSSProperties,
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  } as React.CSSProperties,
  title: (theme: Theme): React.CSSProperties => ({
    fontSize: 24,
    fontFamily: "var(--font-Medium)",
    lineHeight: "33px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  subtitle: (theme: Theme): React.CSSProperties => ({
    fontSize: 14,
    fontFamily: "var(--font-Light)",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  closeButton: {
    cursor: "pointer",
    padding: 4,
    marginTop: 2,
  } as React.CSSProperties,
  summaryBar: (pendingCount: number, theme: Theme): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor:
      pendingCount > 0 ? theme.colorTokens.W[10] : theme.colorTokens.P[10],
    borderRadius: 8,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    padding: "8px 12px",
  }),
  summaryLeft: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  } as React.CSSProperties,
  summaryPendingText: (theme: Theme): React.CSSProperties => ({
    fontSize: 12,
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  summaryRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  } as React.CSSProperties,
  hideAllLink: (theme: Theme): React.CSSProperties => ({
    fontSize: 13,
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    cursor: "pointer",
  }),
  approveAllLink: (theme: Theme): React.CSSProperties => ({
    fontSize: 13,
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: "pointer",
  }),
  systemList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  } as React.CSSProperties,
  othersToggleRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "4px 0",
  } as React.CSSProperties,
  othersToggleLine: (theme: Theme): React.CSSProperties => ({
    flex: 1,
    height: 1,
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  othersToggle: (theme: Theme): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  }),

  // SystemRow
  systemRowContainer: (
    theme: Theme,
    isPending: boolean
  ): React.CSSProperties => ({
    border: `1px solid ${
      isPending
        ? theme.colorTokens.W[60]
        : theme.colors_ds.NEUTRAL_BORDER_DEFAULT
    }`,
    borderRadius: 8,
    padding: 12,
  }),
  systemRowHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  } as React.CSSProperties,
  systemRowLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  } as React.CSSProperties,
  arrowIcon: {
    display: "flex",
    alignItems: "center",
    transition: "transform 0.2s",
  } as React.CSSProperties,
  systemName: (theme: Theme): React.CSSProperties => ({
    fontSize: "14px",
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  systemRowRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  } as React.CSSProperties,
  scoreBadge: (color: string): React.CSSProperties => ({
    fontSize: 12,
    fontFamily: "var(--font-Medium)",
    color: color,
    padding: "0 8px",
    borderRadius: 4,
    backgroundColor: `${color}1A`,
  }),
  iconButton: (isSelected: boolean, theme: Theme): React.CSSProperties => ({
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: isSelected
      ? theme.colors_ds.PRIMARY_ICON_MAIN
      : theme.colorTokens.N[30],
    transition: "background-color 0.15s",
  }),

  // Expanded content
  expandedContent: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  } as React.CSSProperties,
  emptyState: (theme: Theme): React.CSSProperties => ({
    fontFamily: "var(--font-Medium)",
    fontSize: 12,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center",
    padding: "8px 0",
  }),
  divider: (theme: Theme): React.CSSProperties => ({
    height: 1,
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
  }),
  biomarkerSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  } as React.CSSProperties,
  sectionLabelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  } as React.CSSProperties,
  sectionLabel: (theme: Theme): React.CSSProperties => ({
    fontSize: 12,
    fontFamily: "var(--font-Regular)",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  sectionWeight: (theme: Theme): React.CSSProperties => ({
    fontFamily: "var(--font-Medium)",
    fontSize: 12,
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  sectionWeightMax: (theme: Theme): React.CSSProperties => ({
    fontFamily: "var(--font-Regular)",
    fontSize: 12,
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
